import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { CalendarDays, Plus, X, Trash2, Video, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  { value: "event", label: "General Event" },
  { value: "group_session", label: "Group Session" },
  { value: "community", label: "Community Meetup" },
  { value: "retreat", label: "Retreat" },
  { value: "workshop", label: "Workshop" },
];

export default function AdminCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", date: "", time: "", end_time: "",
    category: "event", location: "", zoom_link: "", whatsapp_number: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/calendar/events");
      const customEvents = (res.data || []).filter(e => e.source === "event");
      setEvents(customEvents);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const resetForm = () => {
    setForm({ title: "", description: "", date: "", time: "", end_time: "", category: "event", location: "", zoom_link: "", whatsapp_number: "" });
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/admin/calendar/events", form);
      resetForm();
      fetchEvents();
    } catch (err) { /* silent */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await apiClient.delete(`/admin/calendar/events/${id}`); fetchEvents(); }
    catch (err) { /* silent */ }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-calendar">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">Calendar & Events</h1>
            <p className="text-white/40 text-sm">Manage events, sessions, and schedules</p>
          </div>
          <Button onClick={() => showForm ? resetForm() : setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-event-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "New Event"}
          </Button>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="event-form">
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Event title..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="event-title" />
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description..." className="w-full bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[100px] resize-none" data-testid="event-description" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-[#0f1117] border-white/10 text-white rounded-xl" required data-testid="event-date" />
              <Input type="time" value={form.time} onChange={(e) => setForm({...form, time: e.target.value})} className="bg-[#0f1117] border-white/10 text-white rounded-xl" data-testid="event-time" />
              <Input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="bg-[#0f1117] border-white/10 text-white rounded-xl" placeholder="End time" data-testid="event-end-time" />
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="event-category">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Location (e.g., Zoom, Virtual)" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="event-location" />
              <Input value={form.zoom_link} onChange={(e) => setForm({...form, zoom_link: e.target.value})} placeholder="Zoom link (optional)" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="event-zoom" />
              <Input value={form.whatsapp_number} onChange={(e) => setForm({...form, whatsapp_number: e.target.value})} placeholder="WhatsApp number (optional)" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="event-whatsapp" />
            </div>
            <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl" data-testid="event-submit">
              {saving ? "Creating..." : "Create Event"}
            </Button>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : events.length > 0 ? (
          <div className="space-y-3">
            {events.map(e => (
              <div key={e.event_id} className="bg-[#161821] border border-white/5 rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`cal-evt-${e.event_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-400">{e.category}</span>
                    <span className="text-white/30 text-xs">{e.date} {e.time && `at ${e.time}`}</span>
                    {e.location && <span className="text-white/20 text-xs">{e.location}</span>}
                  </div>
                  <h3 className="text-white font-medium mb-1">{e.title}</h3>
                  {e.description && <p className="text-white/50 text-sm">{e.description}</p>}
                  <div className="flex gap-2 mt-2">
                    {e.zoom_link && (
                      <a href={e.zoom_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-400 hover:underline">
                        <Video className="w-3 h-3" /> Zoom
                      </a>
                    )}
                    {e.whatsapp_number && (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => handleDelete(e.event_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <CalendarDays className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30">No custom events yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
